import { FC, useMemo, useState } from 'react';

import {
  SendCourtesyMessageResult,
  sendCourtesyMessage,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  giftCouponRecipientUrl,
  giftRecipientBaseUrl,
} from '@shared/utils/giftRecipientLink.util';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import { fmtPhone, redeemStatusLabel } from './constants';
import {
  COURTESY_RESEND_MESSAGE,
  LMS_MAX_BYTES,
  buildMournerCourtesyPreset,
  lmsByteLength,
} from './courtesyMessagePresets';
import { SendRoleLegend } from './SendRoleLegend';
import { GiftItemEmbed } from './types';

interface CourtesyMessageModalProps {
  /** 답례 발송 후보 — 수신번호(확인번호) 있는 쿠폰 전체. 이 목록 안에서 골라 보낸다 */
  coupons: GiftCouponSummary[];
  /** 주문 — "상주 대리" 문구(상주 답례글·고인명·상주명) 출처. 없으면 그 문구만 비활성 */
  order?: GiftOrderForAdmin;
  /** 쿠폰 → 상품 임베드 (목록에 상품명 표기용) */
  giftItemEmbedMap?: Map<string, GiftItemEmbed>;
  onClose: () => void;
  /** 발송 성공 후(닫기 시) 호출 — 부모가 선택/모드를 정리한다 */
  onSent?: () => void;
}

const REASON_LABEL: Record<string, string> = {
  NO_PHONE: '수신번호 없음',
  NOT_FOUND: '쿠폰 없음',
};

/**
 * 답례 인사 발송 모달 — 수신자 선택 + 본문 입력 + 발송 결과.
 * 후보 전체를 체크 목록으로 보여주고 기본은 전부 선택. 원하는 사람만 남기고
 * 해제하거나, 번호·메모(수령인)로 걸러 찾은 뒤 선택할 수 있다.
 * (예전엔 표의 체크 컬럼으로만 골랐는데 눈에 안 띄어 "번호 있으면 전부 발송"으로 오해됨)
 */
export const CourtesyMessageModal: FC<CourtesyMessageModalProps> = ({
  coupons,
  order,
  giftItemEmbedMap,
  onClose,
  onSent,
}) => {
  const [message, setMessage] = useState('');
  // 답례품 받기 링크의 프론트 도메인 — 기본값은 현재 접속 도메인.
  // 수신자 전용 도메인(REACT_APP_GIFT_RECIPIENT_BASE_URL)이 설정된 환경에서는 서버가
  // 자체 env(GIFT_RECIPIENT_BASE_URL)로 단축경로 링크를 조립하며 이 값은 무시된다.
  // (env 미설정 구서버 폴백용으로 계속 전송)
  const [baseUrl, setBaseUrl] = useState(window.location.origin);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendCourtesyMessageResult | null>(null);

  // 수신자 선택 — 기본 전체 선택, 개별 해제/선택 가능
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(coupons.map((c) => c._id)),
  );
  const [filter, setFilter] = useState('');

  // 자주 쓰는 문구 — 누르면 본문에 들어간다 (넣은 뒤 자유 수정 가능)
  const mournerPreset = useMemo(() => buildMournerCourtesyPreset(order), [order]);
  const presets = useMemo(
    () => [
      {
        key: 'resend',
        label: '재발송 안내',
        hint: '답례품센터 명의 · 신청 링크 다시 안내',
        text: COURTESY_RESEND_MESSAGE as string | undefined,
      },
      {
        key: 'mourner',
        label: '상주 대리',
        hint: !mournerPreset
          ? '주문 정보 없음'
          : mournerPreset.source === 'saved'
          ? '상주가 저장한 답례글 + 사용안내'
          : '표준 답례글(상주 저장분 없음) + 사용안내',
        text: mournerPreset?.text,
      },
    ],
    [mournerPreset],
  );

  // 직접 쓰던 글을 오클릭으로 날리지 않게, 문구 그대로가 아닌 본문은 덮어쓰기 전에 묻는다
  const applyPreset = (text: string) => {
    if (text === message) return;
    const typed = message.trim();
    const isUntouchedPreset = presets.some((p) => p.text === message);
    if (
      typed &&
      !isUntouchedPreset &&
      !window.confirm('작성 중인 본문을 지우고 선택한 문구로 바꿀까요?')
    ) {
      return;
    }
    setMessage(text);
    setError(null);
  };

  // couponId → 코드/번호 역참조 (결과 표시용)
  const couponById = useMemo(() => {
    const map = new Map<string, GiftCouponSummary>();
    for (const c of coupons) map.set(c._id, c);
    return map;
  }, [coupons]);

  // 번호(숫자만)·메모(수령인)·코드로 목록 걸러보기
  const visibleCoupons = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return coupons;
    const qDigits = q.replace(/\D/g, '');
    return coupons.filter((c) => {
      const phoneDigits = (c.additionalRecipientPhone ?? '').replace(/\D/g, '');
      if (qDigits && phoneDigits.includes(qDigits)) return true;
      if ((c.memo ?? '').toLowerCase().includes(q)) return true;
      return c.code.toLowerCase().includes(q);
    });
  }, [coupons, filter]);

  const selectedCoupons = useMemo(
    () => coupons.filter((c) => selectedIds.has(c._id)),
    [coupons, selectedIds],
  );
  const sendableCount = selectedCoupons.length;
  const visibleSelectedCount = visibleCoupons.filter((c) =>
    selectedIds.has(c._id),
  ).length;
  const allVisibleSelected =
    visibleCoupons.length > 0 && visibleSelectedCount === visibleCoupons.length;

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 전체선택은 "지금 표시된" 목록 기준 — 걸러본 사람들만 한 번에 넣고 뺄 수 있게
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const c of visibleCoupons) next.delete(c._id);
      } else {
        for (const c of visibleCoupons) next.add(c._id);
      }
      return next;
    });
  };

  const trimmedBase = baseUrl.trim().replace(/\/+$/, '');
  const sampleCoupon = selectedCoupons[0] ?? coupons[0];
  // 링크는 쿠폰 code 기반 (서버가 couponIds → code 매핑). 요청은 _id 그대로 전송.
  const previewLink = giftRecipientBaseUrl
    ? giftCouponRecipientUrl(sampleCoupon?.code ?? '{couponCode}')
    : sampleCoupon
    ? `${trimmedBase || '{baseUrl}'}/gift-store/coupon/${sampleCoupon.code}`
    : `${trimmedBase || '{baseUrl}'}/gift-store/coupon/{couponCode}`;

  // 실제 발송 본문(본문 + 자동으로 붙는 링크) 기준 LMS 바이트 수
  const contentBytes = lmsByteLength(`${message.trim()}\n\n답례품 받기\n${previewLink}`);
  const overLimit = contentBytes > LMS_MAX_BYTES;

  const handleSend = async () => {
    if (sendableCount === 0) {
      setError('발송할 수신자를 선택해주세요.');
      return;
    }
    if (!message.trim()) {
      setError('답례글 본문을 입력해주세요.');
      return;
    }
    if (!baseUrl.trim()) {
      setError('답례품 받기 링크 도메인(baseUrl)을 입력해주세요.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await sendCourtesyMessage({
        couponIds: selectedCoupons.map((c) => c._id),
        message: message.trim(),
        baseUrl: baseUrl.trim(),
      });
      setResult(res);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '답례 인사 발송에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onSent?.();
    onClose();
  };

  const labelOf = (couponId: string) => {
    const c = couponById.get(couponId);
    return c ? `#${c.seq} · ${c.code}` : couponId;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div
        className={`relative flex max-h-[88vh] w-full flex-col gap-4 overflow-y-auto rounded-xl bg-white p-5 shadow-2xl ${
          result ? 'max-w-lg' : 'max-w-4xl'
        }`}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-rose-800">답례 인사 발송 (선택형)</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              수신번호 있는 {coupons.length}건 중 선택한{' '}
              <span className="font-bold text-rose-600">{sendableCount}건</span>에 답례글
              LMS를 발송합니다. 상품은 받는 분이 링크에서 직접 신청(선택)합니다.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <SendRoleLegend current="courtesy" />

        {!result ? (
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              {/* 수신자 선택 */}
              <div className="rounded-lg border border-rose-200">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-rose-100 bg-rose-50 px-3 py-2">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      disabled={visibleCoupons.length === 0}
                      className="h-4 w-4 rounded border-rose-300 text-rose-600 focus:ring-rose-500"
                    />
                    {filter.trim() ? '표시된 항목 전체선택' : '전체선택'}
                    <span className="text-xs font-semibold text-rose-600">
                      {sendableCount}/{coupons.length}건 선택
                    </span>
                  </label>
                  <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="번호·수령인 메모·코드로 찾기"
                    className="w-44 rounded-md border border-rose-200 bg-white px-2 py-1 text-xs focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                  />
                </div>
                <ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto">
                  {visibleCoupons.length === 0 && (
                    <li className="px-3 py-3 text-center text-xs text-gray-400">
                      {coupons.length === 0
                        ? '수신번호 있는 쿠폰이 없습니다.'
                        : '검색 결과가 없습니다.'}
                    </li>
                  )}
                  {visibleCoupons.map((c) => {
                    const checked = selectedIds.has(c._id);
                    const canceled = c.redeemStatus === GiftCouponRedeemStatus.Canceled;
                    const productName = giftItemEmbedMap?.get(c.giftItem)?.name;
                    return (
                      <li key={c._id}>
                        <label
                          className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-xs hover:bg-rose-50/60 ${
                            checked ? '' : 'text-gray-400'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(c._id)}
                            className="h-4 w-4 shrink-0 rounded border-rose-300 text-rose-600 focus:ring-rose-500"
                          />
                          <span className="w-8 shrink-0 tabular-nums text-gray-400">
                            #{c.seq}
                          </span>
                          <span
                            className={`w-28 shrink-0 font-mono ${
                              checked ? 'text-amber-700' : ''
                            }`}
                          >
                            {fmtPhone(c.additionalRecipientPhone)}
                          </span>
                          <span className="min-w-0 flex-1 truncate" title={c.memo}>
                            {c.memo ? (
                              <span className={checked ? 'text-gray-800' : ''}>
                                {c.memo}
                              </span>
                            ) : (
                              <span className="text-gray-300">메모 없음</span>
                            )}
                            {productName && (
                              <span className="ml-1.5 text-[10px] text-gray-400">
                                {productName}
                              </span>
                            )}
                          </span>
                          <span
                            className={`shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold ${
                              canceled
                                ? 'bg-red-50 text-red-500'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {redeemStatusLabel[c.redeemStatus]}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* 본문 입력 */}
              <label className="block text-sm">
                <span className="font-medium text-gray-700">답례글 본문</span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={8}
                  placeholder={
                    '예) 감사의 인사를 드립니다.\n따뜻한 마음에 감사드립니다.\n홍길동 드림'
                  }
                  className="mt-1 w-full resize-y rounded-lg border px-3 py-2 text-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
                <span className="mt-0.5 flex flex-wrap justify-between gap-x-2 text-xs text-gray-400">
                  <span>
                    줄바꿈 포함 가능 · 본문 아래에 "답례품 받기" 링크가 자동으로 붙습니다.
                  </span>
                  <span
                    className={`tabular-nums ${
                      overLimit ? 'font-bold text-red-600' : ''
                    }`}
                  >
                    {contentBytes.toLocaleString()} / {LMS_MAX_BYTES.toLocaleString()}byte
                  </span>
                </span>
                {overLimit && (
                  <span className="mt-0.5 block text-xs font-medium text-red-600">
                    LMS 한도 초과 — 발송 실패할 수 있습니다. 본문을 줄여주세요.
                  </span>
                )}
              </label>

              {/* baseUrl — 수신자 전용 도메인 환경에서는 서버가 도메인을 결정하므로 입력 불필요 */}
              {giftRecipientBaseUrl ? (
                <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  답례품 받기 링크는 수신자 전용 도메인(
                  <span className="font-mono font-semibold">{giftRecipientBaseUrl}</span>
                  )으로 발송됩니다.
                </div>
              ) : (
                <label className="block text-sm">
                  <span className="font-medium text-gray-700">
                    답례품 받기 링크 도메인
                  </span>
                  <input
                    type="text"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://bugo.gaonprime.com"
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                  />
                </label>
              )}

              {/* 실제 발송 문자 미리보기 */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                <p className="mb-1 text-xs font-semibold text-gray-500">
                  실제 발송 문자 미리보기 (LMS · 제목 "답례 인사")
                </p>
                <div className="whitespace-pre-wrap break-words text-gray-700">
                  {message.trim() || <span className="text-gray-400">{'{본문}'}</span>}
                  {'\n\n답례품 받기\n'}
                  <span className="text-rose-600">{previewLink}</span>
                </div>
                {sendableCount > 1 && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    링크의 쿠폰 코드는 수신자별로 각각 다르게 들어갑니다. (예시는{' '}
                    {sampleCoupon ? labelOf(sampleCoupon._id) : '{couponCode}'})
                  </p>
                )}
              </div>

              {error && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                  {error}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={onClose}
                  disabled={submitting}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  닫기
                </button>
                <button
                  onClick={handleSend}
                  disabled={submitting || sendableCount === 0}
                  className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-50"
                >
                  {submitting ? '발송중…' : `${sendableCount}건 발송`}
                </button>
              </div>
            </div>

            {/* 자주 쓰는 문구 — 우측 패널. 누르면 좌측 본문에 그대로 들어간다 */}
            <aside className="order-first shrink-0 md:sticky md:top-0 md:order-none md:w-80">
              <p className="text-sm font-medium text-gray-700">자주 쓰는 문구</p>
              <p className="text-xs text-gray-400">
                누르면 본문에 들어갑니다 · 넣은 뒤 수정 가능
              </p>
              <div className="mt-2 flex flex-col gap-2">
                {presets.map((p) => {
                  const active = !!p.text && p.text === message;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      disabled={!p.text}
                      aria-pressed={active}
                      onClick={() => p.text && applyPreset(p.text)}
                      className={`rounded-lg border px-3 py-2 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                        active
                          ? 'border-rose-500 bg-rose-50 ring-1 ring-rose-500'
                          : 'border-gray-200 bg-white hover:border-rose-300 hover:bg-rose-50/50'
                      }`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span
                          className={`text-sm font-bold ${
                            active ? 'text-rose-700' : 'text-gray-800'
                          }`}
                        >
                          {p.label}
                        </span>
                        <span
                          className={`shrink-0 text-[11px] font-semibold ${
                            active ? 'text-rose-600' : 'text-gray-400'
                          }`}
                        >
                          {active ? '적용됨' : '본문에 넣기'}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-[11px] leading-snug text-gray-500">
                        {p.hint}
                      </span>
                      {p.text && (
                        <span className="relative mt-1.5 block max-h-36 overflow-hidden whitespace-pre-wrap break-words rounded-md bg-gray-50 px-2 py-1.5 text-[11px] leading-relaxed text-gray-600">
                          {p.text}
                          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-gray-50" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </aside>
          </div>
        ) : (
          /* 발송 결과 */
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-emerald-50 px-2 py-3">
                <p className="text-xs text-emerald-600">발송 성공</p>
                <p className="text-xl font-bold text-emerald-700">{result.sentCount}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-3">
                <p className="text-xs text-gray-500">건너뜀</p>
                <p className="text-xl font-bold text-gray-600">{result.skippedCount}</p>
              </div>
              <div className="rounded-lg bg-red-50 px-2 py-3">
                <p className="text-xs text-red-500">실패</p>
                <p className="text-xl font-bold text-red-600">{result.failedCount}</p>
              </div>
            </div>
            <p className="text-center text-xs text-gray-400">
              총 {result.total}건 처리 (중복 제거 후)
            </p>

            {result.skipped.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="mb-1 text-xs font-semibold text-gray-500">
                  건너뛴 쿠폰 ({result.skipped.length})
                </p>
                <ul className="space-y-0.5 text-xs text-gray-600">
                  {result.skipped.map((s) => (
                    <li key={s.couponId} className="flex justify-between gap-2">
                      <span>{labelOf(s.couponId)}</span>
                      <span className="text-gray-400">
                        {REASON_LABEL[s.reason] ?? s.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.failed.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                <p className="mb-1 text-xs font-semibold text-red-600">
                  발송 실패 ({result.failed.length})
                </p>
                <ul className="space-y-0.5 text-xs text-red-700">
                  {result.failed.map((f) => (
                    <li key={f.couponId} className="flex justify-between gap-2">
                      <span>{labelOf(f.couponId)}</span>
                      <span className="truncate text-red-400" title={f.reason}>
                        {f.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleDone}
                className="rounded-lg bg-rose-600 px-5 py-2 text-sm font-bold text-white hover:bg-rose-700"
              >
                확인
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
